import { useEffect, useRef, type MutableRefObject } from 'react';
import { useController, type Control } from 'react-hook-form';
import { Alert, Label, RichTextEditor, TextArea, ToggleGroup } from '@/shared/components';
import { useBodyEditorMode, type BodyEditorMode } from '../../hooks/useBodyEditorMode';
import { useInsertAtCursor } from '../../hooks/useInsertAtCursor';
import type { DraftFormValues } from '../../schemas/documentTemplate.schema';
import { TEMPLATE_EDITOR_CONFIG } from '../../utils/visualEditorSupport';

interface Props {
  control: Control<DraftFormValues>;
  disabled: boolean;
  insertRef?: MutableRefObject<((token: string) => void) | null>;
}

const BODY_ID = 'draft-body';
const BODY_ERROR_ID = 'draft-body-error';
const VISUAL_UNAVAILABLE_ID = 'draft-body-visual-unavailable';

export function TemplateBodyField({ control, disabled, insertRef }: Props) {
  const { field, fieldState } = useController({ name: 'bodyHtml', control });
  const { mode, visualSupported, showVisual, showHtml } = useBodyEditorMode(field.value);
  const visualInsert = useRef<((token: string) => void) | null>(null);
  const insertIntoHtml = useInsertAtCursor(BODY_ID, () => field.value, field.onChange);

  useEffect(() => {
    if (!insertRef) return undefined;
    insertRef.current = (token) => (mode === 'visual' ? visualInsert.current?.(token) : insertIntoHtml(token));
    return () => {
      insertRef.current = null;
    };
  });

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Label htmlFor={mode === 'html' ? BODY_ID : undefined}>
          {mode === 'html' ? 'Template body (HTML)' : 'Template body'}
        </Label>
        <ToggleGroup<BodyEditorMode>
          ariaLabel="Editor view"
          value={mode}
          onChange={(next) => (next === 'visual' ? showVisual() : showHtml())}
          options={[
            { value: 'visual', label: 'Visual', disabled: !visualSupported, describedBy: VISUAL_UNAVAILABLE_ID },
            { value: 'html', label: 'HTML' },
          ]}
        />
      </div>

      {mode === 'html' && !visualSupported && (
        <Alert variant="info" role="status">
          <span id={VISUAL_UNAVAILABLE_ID}>This template uses formatting the visual editor cannot keep. Edit it in HTML view.</span>
        </Alert>
      )}

      {mode === 'visual' ? (
        <RichTextEditor
          id={BODY_ID}
          ariaLabel="Template body"
          value={field.value}
          disabled={disabled}
          invalid={!!fieldState.error}
          errorId={fieldState.error ? BODY_ERROR_ID : undefined}
          onChange={field.onChange}
          onBlur={field.onBlur}
          config={TEMPLATE_EDITOR_CONFIG}
          onUnsupportedValue={showHtml}
          insertRef={visualInsert}
          stickyToolbar
        />
      ) : (
        <TextArea
          id={BODY_ID}
          rows={20}
          className="font-mono"
          placeholder="<p>Dear {{customer.name}},</p>"
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
          error={!!fieldState.error}
          hint={fieldState.error?.message}
        />
      )}
      {mode === 'visual' && fieldState.error && (
        <p id={BODY_ERROR_ID} role="alert" className="mt-1.5 text-xs text-mp-error">
          {fieldState.error.message}
        </p>
      )}
    </div>
  );
}
